/**
 * editDataProduk.js — SATU laluan simpan "Data Produk" untuk DUA pintu:
 *   (1) halaman My Products (borang pensel pada item), dan
 *   (2) chip "Edit data" pada kad Workspace (PLAN_EDIT_DATA_KAD.md, 2026-09-12).
 *
 * Kenapa fail sendiri: logik ini dahulu hidup dalam `handleSaveManualData`
 * (LibraryView.jsx). Bila kad Workspace turut boleh menyunting data yang sama,
 * dua salinan laluan simpan PASTI terpesong satu hari (cth: satu tolak link
 * Shopee ke kad, satu lagi tidak) dan kegagalannya senyap. Satu takrifan sahaja.
 *
 * ⛔ Fail ini TIDAK menyentuh React state — pemanggil yang mengemas kini
 *    paparan tempatan daripada nilai pulangan. Ia juga TIDAK memuat naik
 *    gambar; borang sudah memuat naik dan menghantar URL akhir.
 */
import { db } from '../firebase';
import {
    collection, doc, getDoc, getDocs, setDoc, query, where, updateDoc, writeBatch, serverTimestamp, deleteField,
    getCountFromServer,
} from 'firebase/firestore';
import { saveManualProductData, evaluateProductData, buildIntelligenceFromScrape } from '../services/productReadyService';
import { saveLibraryPayload } from '../services/libraryPayloadService';
import { productDetailDoc } from '../services/productService';
import { extractTikTokProductId } from './productDisplayId';
import { kunciShopee } from './shopeeImport';

/* Pemalar yang dikongsi borang + laluan simpan. Nilai SAMA dengan
   `MAKS_TAMBAHAN` dalam gambarTambahan.js / agentGambar.js (had 5 gambar
   termasuk utama). */
export const MAKS_GAMBAR_TAMBAHAN_KAD = 4;

/**
 * Bina patch medan identiti daripada nilai borang. `undefined` = borang lama
 * (rupa lama) yang tidak menghantar medan itu — jangan sentuh.
 * Diasingkan supaya laluan item DAN laluan kad membina bentuk yang SAMA.
 */
export function binaIdPatch({ name, imageUrl, link, shopeeLink, number, originalPrice, discount, extraImageUrls }) {
    const idPatch = {};
    if (name !== undefined) idPatch.name = String(name).trim();
    if (imageUrl !== undefined) idPatch.imageUrl = String(imageUrl).trim();
    if (link !== undefined) idPatch.link = String(link).trim();
    if (shopeeLink !== undefined) idPatch.shopeeLink = String(shopeeLink).trim();
    if (number !== undefined) {
        const n = String(number ?? '').replace(/\D/g, '');
        // Nilai panjang (cth productId 19 digit) disimpan sebagai teks —
        // parseInt akan hilang ketepatan. Corak sama seperti borang lama.
        idPatch.number = !n ? 0 : (n.length <= 15 ? parseInt(n, 10) : n);
    }
    if (originalPrice !== undefined) idPatch.originalPrice = String(originalPrice ?? '').trim();
    if (discount !== undefined) idPatch.discount = String(discount ?? '').trim();
    /* Gambar tambahan untuk Flow — senarai penuh dari borang (galeri TikTok +
       peranti, maks 4). `undefined` = borang lama / tiada kebenaran → jangan sentuh. */
    if (Array.isArray(extraImageUrls)) {
        idPatch.extraImageUrls = extraImageUrls
            .filter((u) => typeof u === 'string' && u)
            .slice(0, MAKS_GAMBAR_TAMBAHAN_KAD);
    }
    return idPatch;
}

/**
 * Tolak link Shopee ke SEMUA kad Workspace produk sama (padan ikut link TikTok; item Shopee
 * tanpa link TikTok → ikut rujukan Shopee, lihat nota 2026-10-06 di dalam).
 * "My Products yang menang" (keputusan user 2026-09-04). Termasuk kes
 * DIKOSONGKAN (kad ikut kosong). Fail-open: gagal tolak ≠ gagal simpan item.
 * @returns bilangan kad yang berubah (0 bila tiada / gagal)
 */
export async function tolakShopeeKeKad({ uid, tiktokLink, shopeeLink, shopeeProductUrl, shopeeLinkLama }) {
    const link = String(tiktokLink || '').trim();
    /* ★ 2026-10-06 — ITEM SHOPEE (link TikTok SENGAJA kosong, utils/shopeeImport.js). Dahulu fungsi
       ini terus pulang 0 bila link TikTok kosong: link affiliate yang ditampal KEMUDIAN di My
       Products tidak pernah sampai ke kad Shopee, dan reel Facebook naik tanpa link affiliate
       (audit Shopee S4). Kini kad Shopee dipadankan ikut:
         · `shopeeProductUrl` yang sama — medan yang kad bawa sejak 6 Okt, DAN
         · link affiliate LAMA yang sama tepat (kad sebelum 6 Okt hanya membawa `shopeeLink`).
       Kad lama yang tiada KEDUA-DUANYA tidak dapat dikenali — hantar semula dari My Products.
       Item ber-link TikTok: perangai asal, tidak disentuh. */
    const urlShopee = !link && kunciShopee(shopeeProductUrl) ? String(shopeeProductUrl).trim() : '';
    const affLama = !link ? String(shopeeLinkLama || '').trim() : '';
    if (!uid || (!link && !urlShopee && !affLama)) return 0;
    try {
        const nilai = String(shopeeLink ?? '').trim();
        const kadRef = collection(db, 'users', uid, 'products');
        const pertanyaan = link
            ? [query(kadRef, where('link', '==', link))]
            : [
                ...(urlShopee ? [query(kadRef, where('shopeeProductUrl', '==', urlShopee))] : []),
                ...(affLama ? [query(kadRef, where('shopeeLink', '==', affLama))] : []),
            ];
        const hasil = await Promise.all(pertanyaan.map((q) => getDocs(q)));
        const kad = new Map();   // id → doc (nyahpendua: satu kad boleh padan kedua-dua pertanyaan)
        hasil.forEach((snapSatu) => snapSatu.docs.forEach((d) => {
            /* Laluan Shopee: hanya kad TANPA link TikTok. Kad ber-link TikTok yang kebetulan membawa
               link affiliate sama ialah produk "kedua-dua" — ia diurus melalui item TikTok-nya. */
            if (!link && String(d.data().link || '').trim()) return;
            kad.set(d.id, d);
        }));
        if (kad.size === 0) return 0;
        const batch = writeBatch(db);
        let n = 0;
        kad.forEach((d) => {
            if ((d.data().shopeeLink || '') !== nilai) { batch.update(d.ref, { shopeeLink: nilai }); n++; }
        });
        if (n > 0) { await batch.commit(); console.log(`[EditData] shopeeLink dipush ke ${n} kad Workspace`); }
        return n;
    } catch (e) {
        console.warn('[EditData] Gagal push shopeeLink ke kad (diabaikan):', e?.message);
        return 0;
    }
}

/**
 * Simpan borang "Data Produk" ke SATU item My Products.
 * Ini laluan yang dahulu berada dalam LibraryView.handleSaveManualData — kelakuan
 * SAMA huruf demi huruf: (1) tulis medan identiti DAHULU (kalau langkah data
 * gagal, nama/link yang pengguna betulkan tidak hilang), (2) padam gambar lama
 * milik sendiri melalui `padamGambar` pemanggil (ia yang tahu kaunter storan),
 * (3) tolak link Shopee ke kad, (4) jana semula perenggan data + nilai READY.
 *
 * @param {object} p
 * @param {string} p.folderId      folder item (`library/{folderId}/items/{id}`)
 * @param {object} p.item          doc item semasa (perlu `id`, `imageUrl`, `link`, `scrapedData`)
 * @param {string} p.uid           uid pelanggan (untuk tolak ke kad)
 * @param {string} p.email         emel pelanggan (`updatedBy` + penilai READY)
 * @param {object} p.borang        nilai yang borang hantar (bentuk `onSave` CompleteProductDataModal)
 * @param {(url:string)=>Promise<void>} [p.padamGambar]  pemadam fail lama milik sendiri (pilihan)
 * @returns {{ idPatch, ready, scrapedData, pendingReason }}
 */
export async function simpanDataProdukItem({ folderId, item, uid, email, borang, padamGambar }) {
    if (!folderId || !item?.id || !email) throw new Error('Maklumat tidak lengkap untuk menyimpan.');
    const {
        description, price, category, bonus,
        name, imageUrl, link, shopeeLink, number, originalPrice, discount,
        extraImageUrls, extraDibuang,
    } = borang || {};

    const idPatch = binaIdPatch({ name, imageUrl, link, shopeeLink, number, originalPrice, discount, extraImageUrls });

    if (Object.keys(idPatch).length > 0) {
        await updateDoc(doc(db, 'library', folderId, 'items', item.id), {
            ...idPatch,
            updatedAt: serverTimestamp(),
            updatedBy: email,
        });
        if (typeof padamGambar === 'function') {
            /* Gambar bertukar (upload dari borang / pautan lain) → fail lama milik
               sendiri dipadam + kiraan ditolak. */
            if (idPatch.imageUrl !== undefined && idPatch.imageUrl !== (item.imageUrl || '')) {
                await padamGambar(item.imageUrl);
            }
            /* Gambar tambahan yang DIBUANG dari borang → fail milik sendiri dipadam
               (pautan galeri CDN dilangkau oleh pemadam — ia bukan fail kita). */
            for (const u of (Array.isArray(extraDibuang) ? extraDibuang : [])) await padamGambar(u);
        }
    }

    if (shopeeLink !== undefined) {
        await tolakShopeeKeKad({
            uid,
            tiktokLink: link !== undefined ? link : item.link,
            shopeeLink,
            // Item Shopee (tiada link TikTok): kad dicari ikut rujukan Shopee item — lihat tolakShopeeKeKad.
            shopeeProductUrl: item.shopeeProductUrl,
            shopeeLinkLama: item.shopeeLink,
        });
    }

    let hasil;
    try {
        hasil = await saveManualProductData({
            folderId,
            product: item,
            email,
            description,
            price,
            category,
            bonus,
            // Nama/gambar/link yang baru ditulis di atas — penilaian READY
            // mesti melihat nilai BAHARU, bukan salinan lama dalam `item`.
            productPatch: idPatch,
        });
    } catch (e) {
        /* Medan identiti SUDAH tertulis bila langkah data gagal — beritahu pemanggil
           supaya paparan tempatan tidak tertinggal di belakang pangkalan data. */
        if (e && typeof e === 'object') e.idPatch = idPatch;
        throw e;
    }
    const { ready, scrapedData, pendingReason } = hasil;

    return { idPatch, ready, scrapedData, pendingReason };
}

/* ═══════════════════════════════════════════════════════════════════════════
   PINTU (2) — chip "Edit data" pada kad Workspace (PLAN_EDIT_DATA_KAD.md)

   Fakta yang menentukan bentuk kod di bawah (diukur 2026-09-12):
   · Kad dan item My Products TIADA ikatan terus — dipadankan ikut LINK TikTok
     (corak sama tolakShopeeKeKad di atas). Sebab itu ada helper `padanLink`.
   · Kad TIDAK menyimpan medan berasingan (kategori/spesifikasi/ulasan). Ia hanya
     bawa SATU perenggan siap-tulis `intelligenceData.imageAnalysis` dalam subdoc
     teks berat. Medan berasingan hidup pada item (`scrapedData`). Jadi borang
     yang dibuka dari kad membaca item pasangan; mod "kad ini sahaja" menyimpan
     salinan medan pada kad (`scrapedDataKad` dalam subdoc yang sama) supaya
     bukaan seterusnya tidak "kehilangan" edit pelanggan.
   ═══════════════════════════════════════════════════════════════════════════ */

/** Folder My Products — sama dengan `LIBRARY_FOLDER_ID` dalam App.jsx (jambatan tarik ke kad). */
export const FOLDER_MY_PRODUCTS = 'product';

/** Kes kad — menentukan notis + pemilih dalam borang dan laluan simpan. */
export const KES_KAD = Object.freeze({
    OWN: 'own',              // ada item My Products milik pelanggan
    SYSTEM: 'system',        // pasangan hanya produk System (bukan milik pelanggan)
    BARU: 'baru',            // tiada pasangan langsung
    TIADA_LINK: 'tiadaLink', // kad tanpa link TikTok — tiada apa boleh dipadankan
});

/**
 * Dua link merujuk produk yang SAMA? Padan ikut ID produk TikTok bila kedua-dua
 * ada ID (bentuk /view/product/{id} lawan /pdp/{slug}/{id} = produk sama);
 * kalau tidak, padanan teks tepat. Kad tampal manual boleh bawa bentuk lain.
 */
export function padanLink(a, b) {
    const x = String(a || '').trim();
    const y = String(b || '').trim();
    if (!x || !y) return false;
    if (x === y) return true;
    const ix = extractTikTokProductId(x);
    const iy = extractTikTokProductId(y);
    return !!(ix && iy && ix === iy);
}

/** Gabung nilai borang ke dalam scrapedData — salinan TEPAT peraturan
    `saveManualProductData` (productReadyService) supaya mod "kad sahaja" dan
    mod item menghasilkan perenggan yang sama untuk input yang sama.
    ⚠️ KEMBAR: kalau peraturan di sana berubah, ubah di sini juga. */
export function gabungScrapedData(sdAsal, { description, price, category, bonus }) {
    const sd = { ...(sdAsal || {}) };
    sd.description = String(description ?? '').trim();
    sd.price = String(price ?? '').trim();
    sd.category = String(category ?? '').trim();
    if (bonus && typeof bonus === 'object') {
        for (const [kunci, nilai] of Object.entries(bonus)) {
            if (nilai !== undefined) sd[kunci] = nilai;
        }
    }
    sd.manualEntry = true;
    return sd;
}

/**
 * Dua rekod (kad / item) merujuk produk SHOPEE yang sama? ID produk Shopee (dari
 * `shopeeProductUrl`) yang menentukan bila KEDUA-DUA ada; kalau tidak, link affiliate
 * pendek yang sama tepat. Kad sebelum 6 Okt 2026 hanya membawa `shopeeLink`.
 * ⛔ Nilai kosong tidak pernah padan — dua kad tanpa rujukan BUKAN produk yang sama.
 */
export function padanShopee(a, b) {
    const ka = kunciShopee(a?.shopeeProductUrl);
    const kb = kunciShopee(b?.shopeeProductUrl);
    if (ka && kb) return ka === kb;
    const la = String(a?.shopeeLink || '').trim();
    const lb = String(b?.shopeeLink || '').trim();
    return !!(la && lb && la === lb);
}

/**
 * Cari item pasangan sebuah kad. Satu query ikut `addedBy` (corak SAMA halaman
 * My Products — terbukti lulus rules, tiada composite index baru), tapis link di
 * klien. Item `own` DIUTAMAKAN — selepas kes System/Baru disimpan, produk itu ada
 * DUA item dengan link sama (System + own); tanpa keutamaan ini edit kedua jatuh
 * ke kes System semula dan mencipta item pendua.
 * @returns {{ kes, item, maxNumber, folderId }}
 */
export async function cariItemPasangan({ email, link, shopee = null }) {
    const folderId = FOLDER_MY_PRODUCTS;
    const itemsRef = collection(db, 'library', folderId, 'items');
    if (!String(link || '').trim()) {
        /* ★ 2026-10-06 — KAD SHOPEE (link TikTok SENGAJA kosong, lihat utils/shopeeImport.js).
           Dahulu terus pulang TIADA_LINK tanpa mencari apa-apa → borang Edit data dibuka KOSONG
           walaupun item My Products ada data scrape penuh; pelanggan menaip semula penerangan dan
           perenggan AI kehilangan jenama / spesifikasi / ulasan (audit Shopee S2).
           Kini item dicari ikut rujukan SHOPEE untuk MENGISI borang sahaja. Kes KEKAL TIADA_LINK:
           Simpan tetap "kad ini sahaja" — item dan kad lain tidak disentuh. */
        const adaRujukan = !!(kunciShopee(shopee?.shopeeProductUrl) || String(shopee?.shopeeLink || '').trim());
        if (!adaRujukan) return { kes: KES_KAD.TIADA_LINK, item: null, maxNumber: 0, folderId };
        const snapShopee = await getDocs(query(itemsRef, where('addedBy', '==', email)));
        let itemShopee = null;
        snapShopee.docs.forEach((d) => {
            const it = { id: d.id, ...d.data() };
            if (!itemShopee && it.group === 'own' && padanShopee(it, shopee)) itemShopee = it;
        });
        return { kes: KES_KAD.TIADA_LINK, item: itemShopee, maxNumber: 0, folderId, padanShopee: !!itemShopee };
    }
    const ownSnap = await getDocs(query(itemsRef, where('addedBy', '==', email)));
    let maxNumber = 0;
    let own = null;
    ownSnap.docs.forEach((d) => {
        const it = { id: d.id, ...d.data() };
        const n = typeof it.number === 'number' ? it.number : parseInt(it.number, 10);
        /* Hanya nombor susunan biasa. Sesetengah item membawa ID produk TikTok (19 digit)
           dalam medan ini; ia melebihi ketepatan JS, jadi +1 memulangkan nombor yang SAMA
           (item pendua nombor — ditangkap ujian 2026-09-13). */
        if (Number.isSafeInteger(n)) maxNumber = Math.max(maxNumber, n);
        if (!own && it.group === 'own' && padanLink(it.link, link)) own = it;
    });
    if (own) return { kes: KES_KAD.OWN, item: own, maxNumber, folderId };
    const sysSnap = await getDocs(query(itemsRef, where('group', '==', 'system')));
    const sys = sysSnap.docs.map((d) => ({ id: d.id, ...d.data() })).find((it) => padanLink(it.link, link)) || null;
    if (sys) return { kes: KES_KAD.SYSTEM, item: sys, maxNumber, folderId };
    return { kes: KES_KAD.BARU, item: null, maxNumber, folderId };
}

/**
 * Berapa kad LAIN yang "Semua kad + My Products" akan sentuh (2026-10-06 — popup pengesahan).
 * Pertanyaan yang SAMA seperti `tolakKeSemuaKad` (link TEPAT), supaya nombor dalam popup =
 * apa yang Simpan benar-benar tulis. ⚠️ KEMBAR: ubah padanan di sana, ubah di sini juga.
 * Kiraan agregat = 1 bacaan, bukan satu bacaan setiap kad.
 * @returns {Promise<number|null>} null = gagal kira ("tidak diketahui") — pemanggil WAJIB
 *          menganggapnya "mungkin ada" (popup tetap muncul), bukan sifar.
 */
async function kiraKadLain({ uid, kad }) {
    const link = String(kad?.link || '').trim();
    if (!uid || !link) return 0;
    try {
        const snap = await getCountFromServer(query(collection(db, 'users', uid, 'products'), where('link', '==', link)));
        const jumlah = Number(snap.data().count);
        if (!Number.isFinite(jumlah) || jumlah < 0) return null;
        // Kad ini sendiri termasuk dalam kiraan hanya bila link tersimpannya sepadan TEPAT.
        return Math.max(0, jumlah - (kad.link === link ? 1 : 0));
    } catch (e) {
        console.warn('[EditData] Gagal kira kad sama link (popup akan muncul tanpa nombor):', e?.message);
        return null;
    }
}

/**
 * Sediakan segala yang borang perlukan bila dibuka dari kad:
 * kes + item pasangan + objek "produk" untuk borang + senarai group/profile.
 * Medan IDENTITI (nama/gambar/link/harga) datang dari KAD — itu yang pelanggan
 * nampak di skrin. Medan data (`scrapedData`) datang dari salinan kad (mod
 * "kad sahaja" terdahulu) DAHULU, item pasangan kalau tiada.
 * ⚠️ Kegagalan baca MELONTAR — borang tidak boleh dibuka atas data separuh.
 */
export async function sediakanEditDataKad({ uid, email, kad }) {
    if (!uid || !email || !kad?.id) throw new Error('Maklumat tidak lengkap.');
    const [pasangan, userSnap, detailSnap, bilKadLain] = await Promise.all([
        cariItemPasangan({ email, link: kad.link, shopee: { shopeeProductUrl: kad.shopeeProductUrl, shopeeLink: kad.shopeeLink } }),
        getDoc(doc(db, 'users', uid)),
        getDoc(productDetailDoc(uid, kad.id)),
        kiraKadLain({ uid, kad }),   // tidak melontar — null bila gagal
    ]);
    const u = userSnap.exists() ? userSnap.data() : {};
    const kumpulan = Array.isArray(u.libraryGroups) ? u.libraryGroups : [];
    const profil = Array.isArray(u.tiktokProfiles) ? u.tiktokProfiles : [];
    const detail = detailSnap.exists() ? detailSnap.data() : {};
    const sdKad = detail.scrapedDataKad && typeof detail.scrapedDataKad === 'object' ? detail.scrapedDataKad : null;
    const item = pasangan.item;

    const produkBorang = {
        id: kad.id,
        name: kad.name || item?.name || '',
        imageUrl: kad.imageUrl || kad.imageBase64 || item?.imageUrl || '',
        link: kad.link || '',
        shopeeLink: kad.shopeeLink || '',
        /* Halaman produk Shopee — rujukan produk untuk syarat READY (borang + simpanDariKad).
           Kad sebelum 6 Okt tidak membawanya; item pasangan (padanan Shopee) yang mengisinya. */
        shopeeProductUrl: (kunciShopee(kad.shopeeProductUrl) ? String(kad.shopeeProductUrl).trim() : '')
            || (kunciShopee(item?.shopeeProductUrl) ? String(item.shopeeProductUrl).trim() : ''),
        /* Nombor = nombor ITEM My Products (kunci susunan 'Sort: Number'), BUKAN turutan kad
           (originalIndex) — silap ini ditangkap mata 2026-09-12: borang memapar 1 dan Simpan
           akan menimpa nombor item. Tiada item → kosong (item baru dinomborkan sendiri). */
        number: item?.number ?? '',
        price: kad.price || item?.price || '',
        originalPrice: kad.originalPrice || item?.originalPrice || '',
        discount: kad.discount || item?.discount || '',
        extraImageUrls: Array.isArray(kad.extraImageUrls) ? kad.extraImageUrls : (item?.extraImageUrls || []),
        scrapedData: sdKad || item?.scrapedData || {},
        /* Borang memakai bendera ini untuk memilih ayat "Edit" lawan "Lengkapkan". */
        hasIntelligence: !!(sdKad || item?.hasIntelligence || kad.intelligenceData),
    };
    return { ...pasangan, produkBorang, kumpulan, profil, adaSalinanKad: !!sdKad, bilKadLain };
}

/**
 * Tolak medan identiti + perenggan data ke SEMUA kad produk sama.
 * Kad dicari ikut link LAMA (kunci padanan sebelum edit) — kalau pelanggan
 * menukar link, kad-kad lain masih dijumpai, kemudian ditulis link baharu.
 * Setiap kad = 2 tulisan (doc utama + subdoc teks berat); batch ≤400 op.
 * Salinan "kad sahaja" (`scrapedDataKad`) DIBUANG pada setiap kad — selepas
 * ini item My Products ialah sumber, salinan lama hanya akan menyesatkan.
 * `kadIni` = { id, patch } — medan TAMBAHAN untuk SATU kad sahaja (kad yang borangnya dibuka).
 *   Dipakai untuk NAMA: keputusan user 2026-10-06 — "Semua kad" TIDAK menukar nama kad lain
 *   (kad salinan kekal "(2)", "(3)"; kad yang dinamakan sendiri kekal). Pemanggil WAJIB
 *   mengeluarkan `name` daripada `patchKad` dan menghantarnya di sini.
 * @returns {string[]} id kad yang dikemas kini
 */
export async function tolakKeSemuaKad({ uid, linkLama, patchKad, intelligenceData, kadIni = null }) {
    const link = String(linkLama || '').trim();
    if (!uid || !link) return [];
    const snap = await getDocs(query(collection(db, 'users', uid, 'products'), where('link', '==', link)));
    const ids = snap.docs.map((d) => d.id);
    if (ids.length === 0) return [];
    const CHUNK = 200; // 2 op/kad → 400 op/batch
    for (let i = 0; i < ids.length; i += CHUNK) {
        const batch = writeBatch(db);
        ids.slice(i, i + CHUNK).forEach((id) => {
            const tambahan = (kadIni && kadIni.id === id && kadIni.patch) ? kadIni.patch : null;
            batch.update(doc(db, 'users', uid, 'products', id), { ...patchKad, ...(tambahan || {}), lastUpdated: serverTimestamp() });
            batch.set(productDetailDoc(uid, id), {
                ...(intelligenceData ? { intelligenceData } : {}),   // null = data tidak lengkap → kekalkan perenggan lama
                scrapedDataKad: deleteField(),
                updatedAt: serverTimestamp(),
            }, { merge: true });
        });
        await batch.commit();
    }
    return ids;
}

/** Patch doc kad daripada nilai borang — hanya medan yang kad memang bawa. */
function patchKadDariBorang(borang, sd) {
    const id = binaIdPatch(borang);
    const patch = {};
    if (id.name !== undefined) patch.name = id.name;
    if (id.imageUrl !== undefined) { patch.imageUrl = id.imageUrl; patch.imageBase64 = null; }
    if (id.link !== undefined) patch.link = id.link;
    if (id.shopeeLink !== undefined) patch.shopeeLink = id.shopeeLink;
    if (id.originalPrice !== undefined) patch.originalPrice = id.originalPrice;
    if (id.discount !== undefined) patch.discount = id.discount;
    if (id.extraImageUrls !== undefined) patch.extraImageUrls = id.extraImageUrls;
    if (sd && sd.price !== undefined) patch.price = sd.price;
    return patch;
}

/**
 * Simpan borang yang dibuka dari KAD.
 * @param {object} p
 * @param {string} p.uid · p.email
 * @param {object} p.kad            kad Workspace (doc ringan)
 * @param {{kes,item,maxNumber,folderId}} p.pasangan  hasil sediakanEditDataKad
 * @param {object} p.produkBorang   objek yang borang terima (bawa scrapedData sumber)
 * @param {object} p.borang         nilai onSave borang (+ `kad: {skop, profileId, groupId}`)
 * @param {(url:string)=>Promise<void>} [p.padamGambar]
 * @returns {{ skop, kadIds, intelligenceData, scrapedData, itemBaru }}
 */
export async function simpanDariKad({ uid, email, kad, pasangan, produkBorang, borang, padamGambar }) {
    const pilihan = borang?.kad || {};
    const kes = pasangan?.kes || KES_KAD.BARU;
    const skop = (kes === KES_KAD.TIADA_LINK) ? 'kad' : (pilihan.skop === 'kad' ? 'kad' : 'semua');
    const sd = gabungScrapedData(produkBorang?.scrapedData, borang);
    const linkLama = String(kad.link || '').trim();
    /* ⛔ Perenggan data kad HANYA ditimpa bila data borang LULUS syarat READY.
       Kad produk System / kad tanpa pasangan / item PENDING membuka borang dengan medan
       KOSONG (data mereka hanya wujud sebagai perenggan lama). Tanpa pagar ini, Simpan untuk
       membetulkan nama sahaja menimpa perenggan sedia ada SEMUA kad produk itu dengan tajuk
       kosong — senyap, dan Generate seterusnya jalan tanpa data (ditangkap ujian 2026-09-13).
       Data tidak lengkap → medan identiti tetap dikemas kini, perenggan lama dikekalkan. */
    const idAwal = binaIdPatch(borang);
    /* ⚠️ KEMBAR senarai semak borang (`check` dalam CompleteProductDataModal.jsx): medan rujukan
       produk di sini WAJIB sama dengan yang borang hantar ke `evaluateProductData`. Sejak 3 Okt
       "Product ID" dipenuhi oleh link TikTok ATAU link Shopee; dahulu baris ini hanya menghantar
       `link`, jadi kad Shopee (link TikTok kosong) dinilai TIDAK sedia walaupun borang berkata
       READY → perenggan data tidak ditulis, senyap (audit 6 Okt, no. 2). */
    const { ready: dataSedia } = evaluateProductData(sd, {
        name: idAwal.name ?? (kad.name || ''),
        imageUrl: idAwal.imageUrl ?? (kad.imageUrl || ''),
        link: idAwal.link ?? linkLama,
        shopeeLink: idAwal.shopeeLink ?? (kad.shopeeLink || ''),
        shopeeProductUrl: kad.shopeeProductUrl || produkBorang?.shopeeProductUrl || '',
    });
    const intelligenceData = dataSedia ? buildIntelligenceFromScrape(sd, email) : null;

    /* Gambar utama / tambahan yang DIGANTI atau DIBUANG dari borang → fail lama
       milik sendiri dipadam + kaunter turun. Sama peraturan seperti item. */
    const bersihkanGambarLama = async () => {
        if (typeof padamGambar !== 'function') return;
        const gambarBaru = String(borang.imageUrl ?? '').trim();
        const gambarLama = String(produkBorang?.imageUrl || '');
        if (borang.imageUrl !== undefined && gambarBaru !== gambarLama) await padamGambar(gambarLama);
        for (const u of (Array.isArray(borang.extraDibuang) ? borang.extraDibuang : [])) await padamGambar(u);
    };

    // ── MOD "KAD INI SAHAJA" ────────────────────────────────────────────────
    if (skop === 'kad') {
        const patch = patchKadDariBorang(borang, sd);
        /* Kad Shopee yang dicipta sebelum 6 Okt tidak membawa `shopeeProductUrl` (jambatan tidak
           menyalinnya). Bila item pasangan dijumpai, tulis rujukan itu ke kad sekali — nilai disemak
           (mesti URL produk Shopee yang ada ID), dan kad yang sudah ada rujukan tidak ditimpa. */
        const urlShopee = String(produkBorang?.shopeeProductUrl || '').trim();
        if (!kunciShopee(kad.shopeeProductUrl) && kunciShopee(urlShopee)) patch.shopeeProductUrl = urlShopee;
        await updateDoc(doc(db, 'users', uid, 'products', kad.id), { ...patch, lastUpdated: serverTimestamp() });
        await setDoc(productDetailDoc(uid, kad.id), {
            ...(intelligenceData ? { intelligenceData } : {}),
            scrapedDataKad: sd,
            updatedAt: serverTimestamp(),
        }, { merge: true });
        /* ⛔ JANGAN padam fail gambar lama di sini (dibuang 2026-10-06, arahan user). Kad dicipta
           dari My Products menyimpan URL fail yang SAMA seperti item (jambatan "Add products"
           menyalin URL, bukan fail), dan kad salinan (Duplicate) berkongsi URL kad asalnya.
           Dahulu `bersihkanGambarLama()` dipanggil di sini: ganti/buang gambar pada SATU kad
           memadam fail itu dari Storage → gambar pecah pada item My Products + kad lain,
           bercanggah dengan janji mod ini ("My Products dan kad lain tidak disentuh").
           Harga yang diterima: fail yang benar-benar tidak dipakai lagi tinggal dalam storan
           (Optimize sengaja tidak menyentuh users/{uid}/library/). Kaunter storan TIDAK
           ditolak kerana failnya memang masih ada — kekal simetri. */
        return { skop, kadIds: [kad.id], intelligenceData, scrapedData: sd, itemBaru: null };
    }

    // ── MOD "SEMUA KAD + MY PRODUCTS" ───────────────────────────────────────
    let itemBaru = null;
    if (kes === KES_KAD.OWN && pasangan.item) {
        /* Laluan item sedia ada = laluan My Products yang SAMA (pintu 1). Ia
           turut menolak link Shopee ke kad; tolakan penuh di bawah menimpa
           dengan nilai yang sama — tiada percanggahan. */
        /* scrapedData asas = apa yang BORANG paparkan (salinan kad kalau ada, item kalau tiada)
           supaya item dan kad menerima perenggan yang SAMA. */
        /* NAMA item hanya ditukar bila pelanggan memang MENGUBAH medan nama dalam borang
           (2026-10-06). Borang dari kad memapar nama KAD — kad salinan bernama "X (2)"; tanpa
           pagar ini, Simpan tanpa menyentuh nama menamakan semula item My Products kepada
           "X (2)". Item tanpa nama langsung tetap menerima nama borang (syarat READY). */
        const namaDiubah = borang?.name !== undefined
            && String(borang.name).trim() !== String(produkBorang?.name ?? '').trim();
        const itemTiadaNama = !String(pasangan.item.name || '').trim();
        const borangItem = (namaDiubah || itemTiadaNama) ? borang : { ...borang, name: undefined };
        await simpanDataProdukItem({ folderId: pasangan.folderId, item: { ...pasangan.item, scrapedData: produkBorang?.scrapedData || pasangan.item.scrapedData }, uid, email, borang: borangItem, padamGambar });
    } else {
        /* Kes SYSTEM / BARU: cipta item My Products BAHARU (bentuk sama seperti
           import — lihat LibraryView.handleImportSave). Item System TIDAK disentuh. */
        const id = binaIdPatch(borang);
        const link = id.link !== undefined ? id.link : linkLama;
        const { ready, missing } = evaluateProductData(sd, { name: id.name, imageUrl: id.imageUrl, link });
        const itemsRef = collection(db, 'library', pasangan.folderId, 'items');
        const refBaru = doc(itemsRef);
        const payload = {
            link,
            group: 'own',
            scrapedData: sd,
            name: id.name ?? (kad.name || ''),
            imageUrl: id.imageUrl ?? (kad.imageUrl || ''),
            number: (pasangan.maxNumber || 0) + 1,
            customGroup: pilihan.groupId || null,
            profileId: pilihan.profileId || null,
            price: sd.price ?? '',
            originalPrice: id.originalPrice ?? '',
            discount: id.discount ?? '',
            shopeeLink: id.shopeeLink ?? (kad.shopeeLink || ''),
            extraImageUrls: id.extraImageUrls ?? [],
            hasIntelligence: ready,
            pendingReason: ready ? null : `Data tidak lengkap: ${missing.join(', ')}`,
            addedAt: serverTimestamp(),
            addedBy: email,
            updatedAt: serverTimestamp(),
            updatedBy: email,
            sumber: kes === KES_KAD.SYSTEM ? 'salin-system-dari-kad' : 'cipta-dari-kad',
        };
        await setDoc(refBaru, payload);
        if (ready) await saveLibraryPayload(pasangan.folderId, refBaru.id, { intelligenceJson: intelligenceData || buildIntelligenceFromScrape(sd, email), updatedBy: email });
        itemBaru = { id: refBaru.id, ...payload };
        await bersihkanGambarLama();
    }

    /* Tolak ke semua kad. Gagal di sini ≠ gagal simpan item — tetapi pelanggan
       WAJIB tahu (peraturan: kegagalan bising). Ralat dibawa naik dengan tanda. */
    let kadIds = [];
    try {
        /* NAMA = milik setiap kad (keputusan user 2026-10-06): hanya kad yang borangnya dibuka
           menerima nama dari borang; kad lain kekal dengan nama masing-masing. Medan lain
           (gambar, link, harga, perenggan data) tetap ditolak ke SEMUA kad. */
        const { name: namaKadIni, ...patchSemua } = patchKadDariBorang(borang, sd);
        kadIds = await tolakKeSemuaKad({
            uid, linkLama, patchKad: patchSemua, intelligenceData,
            kadIni: namaKadIni !== undefined ? { id: kad.id, patch: { name: namaKadIni } } : null,
        });
    } catch (e) {
        const err = new Error(e?.message || 'Gagal mengemas kini kad');
        err.itemDisimpan = true;
        err.itemBaru = itemBaru;
        throw err;
    }
    return { skop, kadIds, intelligenceData, scrapedData: sd, itemBaru };
}

/* ═══════════════════════════════════════════════════════════════════════════
   DUPLICATE KAD — bawa salinan "kad ini sahaja" ke kad SALINAN (2026-10-06)

   Duplicate (WorkspaceView.handleDuplicateProducts, 2026-08-03) dibina SEBELUM
   `scrapedDataKad` wujud (2026-09-12). Ia menyalin kad melalui laluan simpan biasa,
   yang hanya mengenal prompts / intelligenceData / rawOutput dalam subdoc — jadi
   `scrapedDataKad` tertinggal: borang Edit data pada kad salinan keluar KOSONG +
   PENDING walaupun perenggan datanya ikut disalin (ditangkap user 2026-10-06).

   Dua langkah BERASINGAN: pemanggil MEMBACA sebelum mencipta apa-apa (gagal baca =
   batal seluruh Duplicate, sama peraturan seperti hydrate) dan MENULIS hanya selepas
   kad salinan benar-benar tersimpan (subdoc tanpa doc utama = kad hantu).
   ⚠️ Medan baharu dalam subdoc `detail/text` yang BUKAN prompts/intelligenceData/
      rawOutput tidak ikut Duplicate dengan sendirinya — tambah di sini.
   ═══════════════════════════════════════════════════════════════════════════ */

/** Objek biasa yang berisi sahaja — senarai / kosong / bukan objek ditolak. */
const sdKadSah = (v) => (v && typeof v === 'object' && !Array.isArray(v) && Object.keys(v).length > 0 ? v : null);

/**
 * Baca `scrapedDataKad` beberapa kad. MELONTAR bila mana-mana bacaan gagal.
 * @returns {Promise<Map<string, object>>} hanya kad yang memang ada salinan
 */
export async function bacaDataKadSahaja({ uid, kadIds }) {
    const ids = [...new Set((Array.isArray(kadIds) ? kadIds : []).filter((id) => typeof id === 'string' && id))];
    const hasil = new Map();
    if (!uid || ids.length === 0) return hasil;
    const snaps = await Promise.all(ids.map((id) => getDoc(productDetailDoc(uid, id))));
    snaps.forEach((s, i) => {
        const sd = s.exists() ? sdKadSah(s.data().scrapedDataKad) : null;
        if (sd) hasil.set(ids[i], sd);
    });
    return hasil;
}

/**
 * Tulis `scrapedDataKad` ke subdoc kad SALINAN. Panggil HANYA selepas doc utama
 * kad-kad itu tersimpan. MELONTAR bila tulisan gagal — pemanggil WAJIB memberitahu
 * pelanggan (kad salinan sudah wujud, hanya medan borangnya yang tertinggal).
 * @param {object} p
 * @param {string} p.uid
 * @param {Array<{id:string, scrapedDataKad:object}>} p.senarai
 * @returns {Promise<number>} bilangan kad ditulis
 */
export async function tulisDataKadSalinan({ uid, senarai }) {
    const sah = (Array.isArray(senarai) ? senarai : [])
        .filter((x) => x && typeof x.id === 'string' && x.id && sdKadSah(x.scrapedDataKad));
    if (!uid || sah.length === 0) return 0;
    const CHUNK = 100; // satu salinan boleh berpuluh KB (ulasan + galeri) — jauh di bawah had 10 MB/permintaan
    for (let i = 0; i < sah.length; i += CHUNK) {
        const batch = writeBatch(db);
        sah.slice(i, i + CHUNK).forEach((x) => {
            batch.set(productDetailDoc(uid, x.id), { scrapedDataKad: x.scrapedDataKad, updatedAt: serverTimestamp() }, { merge: true });
        });
        await batch.commit();
    }
    return sah.length;
}
